/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import { type CustomGlyphProps, type Size } from '@atlaskit/icon';
import {
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	type LogoProps,
	TrelloIcon,
} from '@atlaskit/logo';

// This is the only place we want to import this
import { DO_NOT_IMPORT_ProductKeys3P, DO_NOT_IMPORT_ProductsOrder3P } from './3p-product-configs';
import { AirtableIcon } from './icons/airtable';
import { AirtableFilterIcon } from './icons/airtable-filter';
import { AsanaIcon } from './icons/asana';
import { AtlasSearchResultIcon } from './icons/atlas-search';
import { ConfluenceSearchResultIcon } from './icons/confluence-search';
import { DropboxIcon } from './icons/dropbox';
import { FigmaIcon } from './icons/figma';
import { FigmaSearchResultIcon } from './icons/figma-search';
import { GithubIcon } from './icons/github';
import { GithubSearchResultIcon } from './icons/github-search';
import { GmailIcon } from './icons/gmail';
import { GoogleCalendarIcon } from './icons/google-calendar';
import { GoogleDriveIcon } from './icons/google-drive';
import { GoogleDriveSearchResultIcon } from './icons/google-drive-search';
import { JiraSearchResultIcon } from './icons/jira-search';
import { LoomIcon } from './icons/loom';
import { MiroIcon } from './icons/miro';
import { MiroFilterIcon } from './icons/miro-filter';
import { NotionIcon } from './icons/notion';
import { NotionFilterIcon } from './icons/notion-filter';
import { OneDriveIcon } from './icons/onedrive';
import { OutlookCalendarIcon } from './icons/outlook-calendar';
import { SharePointIcon } from './icons/sharepoint';
import { SharePointSearchResultIcon } from './icons/sharepoint-search';
import { SlackIcon } from './icons/slack';
import { SlackSearchResultIcon } from './icons/slack-search';
import { TeamsIcon } from './icons/teams';
import { TeamsSearchResultIcon } from './icons/teams-search';
import { WebDomainIcon } from './icons/web-domain';
import { WebDomainSearchResultIcon } from './icons/web-domain-search';
import { ZendeskIcon } from './icons/zendesk';
import { messages } from './messages';

const iconStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const iconSizeStyles = cssMap({
	small: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '16px',
			height: '16px',
		},
	},
	medium: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '24px',
			height: '24px',
		},
	},
	large: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '32px',
			height: '32px',
		},
	},
});

type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
	[Property in Key]-?: Type[Property];
};

export type IconProps = Omit<
	WithRequiredProperty<LogoProps, 'appearance'>,
	'iconColor' | 'textColor' | 'size'
> & {
	size?: SmallOrMedium;
};

export interface ProductConfig {
	displayName: React.ReactNode;
	// eslint-disable-next-line @typescript-eslint/ban-types
	Icon: FC<IconProps>;
	is3P?: boolean;
	// eslint-disable-next-line @typescript-eslint/ban-types
	SearchResultIcon?: FC<IconProps>;
}

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Opsgenie: 'opsgenie',
	Trello: 'trello',
} as const;

export const ProductKeys3P = DO_NOT_IMPORT_ProductKeys3P;

export const ProductKeys = {
	...ProductKeys1P,
	...DO_NOT_IMPORT_ProductKeys3P,
} as const;

export type ProductKeys3P =
	(typeof DO_NOT_IMPORT_ProductKeys3P)[keyof typeof DO_NOT_IMPORT_ProductKeys3P];
export type ProductKeys1P = (typeof ProductKeys1P)[keyof typeof ProductKeys1P];
export type ProductKeys = (typeof ProductKeys)[keyof typeof ProductKeys];
export const allProductKeys: ProductKeys[] = Object.values(ProductKeys);
export const all3pProductKeys: ProductKeys3P[] = Object.values(DO_NOT_IMPORT_ProductKeys3P);
export const all1pProductKeys: ProductKeys1P[] = Object.values(ProductKeys1P);
export const is3pProductKey = (key: string): key is ProductKeys3P =>
	all3pProductKeys.includes(key as ProductKeys3P);

// This is different to ProductKeys, it represents products we have support for mounting Search Page in
export const PrimaryProductKeys = {
	// Fully supported as GA
	Atlas: 'townsquare',
	Confluence: 'confluence',
	// In development
	Jira: 'jira',
	Mercury: 'mercury',
	Elevate: 'elevate',
} as const;
export type PrimaryProductKey = (typeof PrimaryProductKeys)[keyof typeof PrimaryProductKeys];

/**
 * Due a bug in ADS custom icons are one size smaller
 * To accommodate for this behavior you need to use one size bigger than what you need
 * See: https://atlassian.slack.com/archives/CFJ9DU39U/p1714096323217799
 */
type SmallOrMedium = 'xxsmall' | Exclude<Size, 'large' | 'xlarge'>;
type MediumOrLarge = Exclude<Size, 'xlarge'>;
const sizesCorrection: Record<SmallOrMedium, MediumOrLarge> = {
	xxsmall: 'small',
	small: 'medium',
	medium: 'large',
};

export const getIcon = ({
	label,
	icon,
}: {
	icon: (glyphProps: CustomGlyphProps) => JSX.Element;
	label: string;
}) => {
	return ({ size = 'medium' }: { size?: SmallOrMedium }) => (
		<span css={[iconStyles, iconSizeStyles[sizesCorrection[size]]]}>
			{icon({ 'aria-label': label })}
		</span>
	);
};

/**
 * This is the source of truth for the products that are supported in Full Page Search.
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * QS-5444 3P references in this object are being deprecated. Please add to ./3p-product-configs
 */
export const products: Record<string, ProductConfig> = {
	[ProductKeys.Confluence]: {
		displayName: 'Confluence',
		Icon: ConfluenceIcon,
		SearchResultIcon: getIcon({ icon: ConfluenceSearchResultIcon, label: 'Confluence' }),
	},
	[ProductKeys.Atlas]: {
		displayName: 'Atlas',
		Icon: AtlassianIcon,
		SearchResultIcon: getIcon({
			icon: AtlasSearchResultIcon,
			label: messages.atlas.defaultMessage,
		}),
	},
	[ProductKeys.Jira]: {
		displayName: 'Jira',
		Icon: JiraIcon,
		SearchResultIcon: getIcon({ icon: JiraSearchResultIcon, label: 'Jira' }),
	},
	[ProductKeys.JiraProductDiscovery]: {
		displayName: 'Jira Product Discovery',
		Icon: JiraProductDiscoveryIcon,
	},
	[ProductKeys.JiraServiceManagement]: {
		displayName: 'Jira Service Management',
		Icon: JiraServiceManagementIcon,
	},
	[ProductKeys.Bitbucket]: {
		displayName: 'Bitbucket',
		Icon: BitbucketIcon,
		SearchResultIcon: BitbucketIcon,
	},
	[ProductKeys.Trello]: {
		displayName: 'Trello',
		Icon: TrelloIcon,
	},
	// [ProductKeys.Opsgenie]: {
	// 	displayName: 'Opsgenie',
	// 	Icon: OpsgenieIcon,
	// },
	[ProductKeys.Compass]: {
		displayName: 'Compass',
		Icon: CompassIcon,
	},
	[ProductKeys.GoogleDrive]: {
		displayName: 'Google Drive',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: GoogleDriveIcon, label: 'Google Drive' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: GoogleDriveSearchResultIcon, label: 'Google Drive' }),
	},
	[ProductKeys.Sharepoint]: {
		displayName: 'Microsoft SharePoint',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: SharePointIcon, label: 'Sharepoint' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: SharePointSearchResultIcon, label: 'Sharepoint' }),
	},
	[ProductKeys.AtlassianWebCrawler]: {
		displayName: <FormattedMessage {...messages.webPage} />,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: WebDomainIcon, label: 'Atlassian Web Crawler' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: WebDomainSearchResultIcon, label: 'Atlassian Web Crawler' }),
	},
	[ProductKeys.Teams]: {
		displayName: 'Teams',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: TeamsIcon, label: 'Teams' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: TeamsSearchResultIcon, label: 'Teams' }),
	},
	[ProductKeys.Loom]: {
		displayName: 'Loom',
		Icon: getIcon({ icon: LoomIcon, label: 'Loom' }),
		is3P: true,
	},
	[ProductKeys.OneDrive]: {
		displayName: 'OneDrive',
		Icon: getIcon({ icon: OneDriveIcon, label: 'OneDrive' }),
		is3P: true,
	},
	[ProductKeys.Slack]: {
		displayName: 'Slack',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: SlackIcon, label: 'Slack' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: SlackSearchResultIcon, label: 'Slack' }),
	},
	[ProductKeys.Github]: {
		displayName: 'GitHub',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: GithubIcon, label: 'Github' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: GithubSearchResultIcon, label: 'Github' }),
	},
	[ProductKeys.Figma]: {
		displayName: 'Figma',
		Icon: getIcon({ icon: FigmaIcon, label: 'Figma' }),
		is3P: true,
		SearchResultIcon: getIcon({ icon: FigmaSearchResultIcon, label: 'Figma' }),
	},
	[ProductKeys.Dropbox]: {
		displayName: 'Dropbox',
		Icon: getIcon({ icon: DropboxIcon, label: 'Dropbox' }),
		is3P: true,
	},
	[ProductKeys.GoogleCalendar]: {
		displayName: 'Google Calendar',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: GoogleCalendarIcon, label: 'Google Calendar' }),
		is3P: true,
	},
	[ProductKeys.Gmail]: {
		displayName: 'Gmail',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: GmailIcon, label: 'Gmail' }),
		is3P: true,
	},
	[ProductKeys.OutlookCalendar]: {
		displayName: 'Outlook Calendar',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: OutlookCalendarIcon, label: 'Outlook Calendar' }),
		is3P: true,
	},
	[ProductKeys.Airtable]: {
		displayName: 'Airtable',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: AirtableFilterIcon, label: 'Airtable' }),
		is3P: true,
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		SearchResultIcon: getIcon({ icon: AirtableIcon, label: 'Airtable' }),
	},
	[ProductKeys.Asana]: {
		displayName: 'Asana',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: AsanaIcon, label: 'Asana' }),
		is3P: true,
	},
	[ProductKeys.Zendesk]: {
		displayName: 'Zendesk',
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		Icon: getIcon({ icon: ZendeskIcon, label: 'Zendesk' }),
		is3P: true,
	},
	[ProductKeys.Notion]: {
		displayName: 'Notion',
		Icon: getIcon({ icon: NotionFilterIcon, label: 'Notion' }),
		is3P: true,
		SearchResultIcon: getIcon({ icon: NotionIcon, label: 'Notion' }),
	},
	[ProductKeys.Miro]: {
		displayName: 'Miro',
		Icon: getIcon({ icon: MiroFilterIcon, label: 'Miro' }),
		is3P: true,
		SearchResultIcon: getIcon({ icon: MiroIcon, label: 'Miro' }),
	},
};

export const PRODUCTS_ORDER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Opsgenie,
	ProductKeys.Compass,

	...DO_NOT_IMPORT_ProductsOrder3P,
];
